import React from 'react';
import {
  Paper,
  Title,
  TextInput,
  Textarea,
  Group,
  Button,
  Stack,
  Badge,
  Select,
} from '@mantine/core';
import type { TripFormState, Visibility } from '../../types';
import { FONTS, STATUS_COLORS } from '../../theme/tokens';
import { STATUS_LABELS, VISIBILITY_LABELS } from '../../constants';

export interface EditableSummaryProps {
  headerForm: TripFormState;
  isAdmin: boolean;
  busy: boolean;
  isSaving: boolean;
  onHeaderFormChange: (updater: (prev: TripFormState) => TripFormState) => void;
  onReset: () => void;
  onSave: () => void;
}

const VISIBILITY_OPTIONS: Visibility[] = [
  'CREATOR',
  'ADMINS',
  'MEMBERS',
  'PUBLIC',
];

export const EditableSummary: React.FC<EditableSummaryProps> = ({
  headerForm,
  isAdmin,
  busy,
  isSaving,
  onHeaderFormChange,
  onReset,
  onSave,
}) => {
  const statusColors =
    STATUS_COLORS[headerForm.status] ?? STATUS_COLORS.PLANNING;

  return (
    <Paper shadow="sm" p="lg" radius="md" withBorder>
      <Group justify="space-between" mb="md">
        <Title order={2} style={{ fontFamily: FONTS.serif }}>
          Trip Itinerary
        </Title>
        <Badge
          variant="filled"
          styles={{
            root: {
              backgroundColor: statusColors.bg,
              color: statusColors.text,
            },
          }}
        >
          {STATUS_LABELS[headerForm.status] ?? headerForm.status}
        </Badge>
      </Group>
      <Stack gap="sm">
        <TextInput
          label="Name"
          value={headerForm.name}
          onChange={e =>
            onHeaderFormChange(prev => ({ ...prev, name: e.target.value }))
          }
          disabled={!isAdmin || busy}
          readOnly={!isAdmin}
        />
        <Textarea
          label="Description"
          value={headerForm.description}
          onChange={e =>
            onHeaderFormChange(prev => ({
              ...prev,
              description: e.target.value,
            }))
          }
          disabled={!isAdmin || busy}
          readOnly={!isAdmin}
          rows={3}
        />
        {isAdmin ? (
          <Select
            label="Visibility"
            data={VISIBILITY_OPTIONS.map(v => ({
              value: v,
              label: VISIBILITY_LABELS[v] ?? v,
            }))}
            value={headerForm.visibility}
            onChange={value =>
              onHeaderFormChange(prev => ({
                ...prev,
                visibility: (value as Visibility) ?? prev.visibility,
              }))
            }
            disabled={busy}
          />
        ) : (
          <Badge variant="outline" size="lg">
            {VISIBILITY_LABELS[headerForm.visibility] ?? headerForm.visibility}
          </Badge>
        )}
        {isAdmin && (
          <Group justify="flex-end">
            <Button
              variant="outline"
              onClick={onReset}
              disabled={busy}
              data-cy="itinerary-reset"
            >
              Reset
            </Button>
            <Button
              onClick={onSave}
              loading={isSaving}
              disabled={busy}
              data-cy="itinerary-save-shared"
            >
              Save trip details
            </Button>
          </Group>
        )}
      </Stack>
    </Paper>
  );
};

export default EditableSummary;
